import { isEmpty, isNil } from 'lodash/fp';
import {
    DELETE_PROJECT_OPERATION,
    UPDATE_PROJECT_OWNER_OPERATION,
    UPDATE_PROJECT_NAME_OPERATION,
    UPDATE_VIEW_NAME_OPERATION,
    UPDATE_ASSET_DESCRIPTION_OPERATION
} from 'app/store/__generated__/operations';
import { DATA_SOURCE_REFERENCE_DTO_TYPE } from 'app/store/__generated__/types';
import { doLoadCurrentProject, doLoadProject } from 'app/store/projects/actions.js';
import {
    isProject,
    isWorkbench,
    navigateToWorkbench,
    getCurrentProjectSlug,
    getCurrentViewUid
} from 'app/routes/routes.js';
import { isRedirectAllowed } from 'app/lib/utils/isRedirectAllowed';
import { isCurrentProject, selectSelectedEntityId } from 'app/store/projects/selectors.js';
import { doGetTableDetailsForDataBrowser } from 'app/store/tableDetails/actions.js';
import { doGetSnowflakeDatabases } from 'app/store/snowflake/actions.js';
import {
    doCancelAndDeleteExploreQueries,
    doDeleteDatasetQuery,
    doDeleteRowCountQuery
} from 'app/store/snowflakeQueries/actions.js';
import {
    selectEntity,
    selectEntityByPropId,
    selectFilteredSchemaForPropEntityId
} from './selectors.js';
import {
    DELETE_PROJECT,
    DELETE_VIEW,
    UPDATE_PROJECT_OWNER,
    UPDATE_PROJECT_NAME,
    UPDATE_VIEW_NAME,
    UPDATE_ENTITY_DESCRIPTION,
    GET_COLUMN_PROPERTIES,
    OPEN_DELETE_ENTITY_DIALOG,
    CLOSE_DELETE_ENTITY_DIALOG,
    DELETE_UNUSED_DATASETS
} from './types.js';

export const handleViewDeleted = (dispatch, state, properties) => {
    const { spaceSlug, spaceUid, sessionId, entityToDelete, isViewingDeletedEntity } = properties;
    const { _type, tableFqn, entityId } = entityToDelete;

    dispatch(doDeleteDatasetQuery({ entityId }));
    dispatch(doDeleteRowCountQuery({ fqnOrId: entityId }));
    dispatch(doCancelAndDeleteExploreQueries(entityId));

    if (isProject() && isCurrentProject(spaceUid, state)) {
        dispatch(doLoadProject({ projectSlug: spaceSlug }));

        if (_type === DATA_SOURCE_REFERENCE_DTO_TYPE) {
            dispatch(doGetTableDetailsForDataBrowser({ tables: [tableFqn] }));
        }
    }

    if (isRedirectAllowed(sessionId)) {
        if (isWorkbench()) {
            const entity = selectEntity(state);

            if (!entity) {
                const { sources, deployedFrom } = entityToDelete;
                let viewUid = null;

                if (isViewingDeletedEntity) {
                    if (sources) {
                        viewUid = isEmpty(sources) ? null : sources[0].uid;
                    } else if (deployedFrom) {
                        viewUid = selectEntityByPropId(state, { entityId: deployedFrom }).uid;
                    }
                }

                navigateToWorkbench(spaceSlug, viewUid);
            }
        }
    }
};

export const doOpenDeleteAssetDialog = (assetId) => ({
    type: OPEN_DELETE_ENTITY_DIALOG,
    params: { assetId }
});

export const doCloseDeleteEntityDialog = () => ({
    type: CLOSE_DELETE_ENTITY_DIALOG
});

export const doDeleteProject = (projectUid) => ({
    type: DELETE_PROJECT,
    api: DELETE_PROJECT_OPERATION,
    params: { spaceUid: projectUid, ignoreWarnings: true },
    onSucceeded: ({ dispatch }) => {
        dispatch(doGetSnowflakeDatabases());
    }
});

export const doDeleteView = (assetId, ignoreWarnings) => {
    let entityToDelete;
    return {
        type: DELETE_VIEW,
        api: { version: 2, name: 'DeleteView' },
        params: (state) => {
            entityToDelete = selectEntityByPropId(state, { entityId: assetId });
            return { assetId, ignoreWarnings };
        },
        onSucceeded: ({ dispatch, getState, payload, meta }) => {
            const { spaceSlug, spaceUid } = payload.viewRef;
            const properties = {
                sessionId: meta.sessionId,
                spaceSlug,
                spaceUid,
                entityToDelete,
                isViewingDeletedEntity: getCurrentViewUid() === entityToDelete?.uid
            };

            handleViewDeleted(dispatch, getState(), properties);
        }
    };
};

export const doDeleteUnusedDatasets = (datasets, spaceUid) => {
    let tableFqns;

    return {
        type: DELETE_UNUSED_DATASETS,
        api: { version: 2, name: 'DeleteUnusedDatasetsFromProject' },
        params: (state) => {
            tableFqns = datasets
                .map((dataset) => selectEntityByPropId(state, { entityId: dataset }))
                .map(({ tableFqn }) => tableFqn);

            return { datasets };
        },
        meta: { projectUid: spaceUid },
        onSucceeded: ({ dispatch, getState }) => {
            dispatch(doLoadProject({ projectSlug: spaceUid }));

            dispatch(doGetTableDetailsForDataBrowser({ tables: tableFqns }));

            if (isNil(selectSelectedEntityId(getState()))) {
                const projectSlug = getCurrentProjectSlug();
                navigateToWorkbench(projectSlug);
            }
        }
    };
};

export const doUpdateProjectOwner = ({ entityId, newOwner, onFinish }) => ({
    type: UPDATE_PROJECT_OWNER,
    api: UPDATE_PROJECT_OWNER_OPERATION,
    params: { spaceUid: entityId, newOwner },
    onFinish
});

export const doUpdateProjectName = ({ entityId, name }) => ({
    type: UPDATE_PROJECT_NAME,
    api: UPDATE_PROJECT_NAME_OPERATION,
    params: { spaceUid: entityId, spaceName: name }
});

export const doUpdateViewName = ({ entityId, name }) => ({
    type: UPDATE_VIEW_NAME,
    api: UPDATE_VIEW_NAME_OPERATION,
    params: { uuid: entityId, name },
    onSucceeded: ({ dispatch }) => {
        dispatch(doLoadCurrentProject(false));
    }
});

export const doUpdateDescription = ({ assetId, description }) => ({
    type: UPDATE_ENTITY_DESCRIPTION,
    api: UPDATE_ASSET_DESCRIPTION_OPERATION,
    params: { assetId, description }
});

export const doGetColumnProperties = (entity) => ({
    type: GET_COLUMN_PROPERTIES,
    api: { version: 2, name: 'GetColumnMetadata' },
    params: (state) => {
        const { _type, entityId, tableFqn } = entity;
        const schema = selectFilteredSchemaForPropEntityId(state, { entityId });
        const columnNames = schema.map(({ name }) => name);
        // fetch attributes of the datasource when it is a DataSourceReference
        const viewUuid = _type === DATA_SOURCE_REFERENCE_DTO_TYPE ? tableFqn : entityId;

        return {
            viewUuid,
            columnNames
        };
    },
    meta: { entityId: entity.tableFqn ?? entity.entityId }
});
